import ENV from "@config/env";
import React from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import Table from "@components/layout/Table";
import Heading from "@components/layout/Heading";
import ConfirmModal from "@components/shared/ConfirmModal";
import DeclarationTag from "@components/shared/DeclarationTag";
import TrialRunTag from "./TrialRunTag";
import TrialRunDetailsModal from "./TrialRunDetailsModal";
import InlineEditCell from "./InlineEditCell";
import RowNoteCell from "./RowNoteCell";
import SubscriberSelectCell from "./SubscriberSelectCell";
import OfftakeSelectCell from "./OfftakeSelectCell";
import { useBulkMeterUploadContext } from "@context/BulkMeterUploadContext";
import { useAllSubscribers } from "@hooks/query/useAllSubscribers";
import { useAllExtractionPoints } from "@hooks/query/useAllExtractionPoints";
import { useStepContext } from "@context/shared/StepContext";
import { useAppContext } from "@context/AppContext";
import { toastError, toastSuccess, toastWarning } from "@utils/toast";
import { getValidationMessages } from "@utils/formError";
import { useEvidenceContext } from "@context/shared/EvidenceContext";
import { flushNoteStepDraft } from "@components/notes/flushNoteStepDraft";
import { convertLiterToML, convertMLToLiter } from "@utils/convertUnits";
import { formatDate } from "@utils/formatDate";
import { formatVolume } from "@utils/formatVolume";
import { downloadAsCSV } from "@utils/downloadAsCSV";
import { READING_CALCULATION } from "@services/declarations";
import { DBTables } from "@utils/constants/dbTables";
import {
  MeterUploadRowStatus,
  TrialRunDisplayStatus,
  type MeterUploadRowData,
  type TrialRunDisplayRow,
  type TrialRunRowErrors,
} from "@utils/bulkMeterUpload/buildTrialRunRow";
import { MeterUploadStatus } from "@utils/bulkMeterUpload/computeParentStatus";
import { orderTrialRunRows } from "@utils/bulkMeterUpload/orderTrialRunRows";
import { isRowEditable } from "@utils/bulkMeterUpload/inlineRowEdit";
import { buildNewRow } from "@utils/bulkMeterUpload/newRow";
import { toSubscriberOptions } from "@utils/bulkMeterUpload/subscriberSelect";

const IS_SEQWATER = ENV.CLIENT_ID === "seqwater";

const TrialRun: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { isExpandInfoPanel } = useAppContext();
  const [submitting, setSubmitting] = React.useState(false);
  // False until the first trial run settles. Gates the full-grid spinner so it
  // covers the whole mount → debounce → request → result gap (otherwise the raw,
  // unvalidated rows flash for the 300ms debounce before the spinner appears).
  // Stays true afterwards so later re-checks never blank the grid being edited.
  const [hasRunTrial, setHasRunTrial] = React.useState(false);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const [viewDetailRowId, setViewDetailRowId] = React.useState<string | null>(
    null,
  );
  const [deletingRowId, setDeletingRowId] = React.useState<string | null>(null);
  const [isDeletingRow, setIsDeletingRow] = React.useState(false);
  // Drives the post-ledger acknowledgment modal. Closing it redirects (handleCancel);
  // the detached finalize runs in the background and notifies the operator when done.
  const [ledgerStarted, setLedgerStarted] = React.useState(false);
  const { stepHelpers } = useStepContext();
  const { evidences: files, uploadEvidences } = useEvidenceContext();
  const {
    level1WRS,
    rows,
    displayRows,
    parentStatus,
    runTrialRun,
    saveDraft,
    ledger,
    applyManualIntervention,
    addRow,
    editRow,
    deleteRow,
    meterUploadId,
    isLedgering,
    isTrialRunning,
    handleCancel,
    draftNote,
    readOnly,
  } = useBulkMeterUploadContext();

  // Subscribers for the account-cell select, scoped to the upload's scheme and
  // fetched once for the whole grid (one lookup, not one per row — react-select
  // options are then shared). Each option's value is the account number it fills.
  const { data: subscribers } = useAllSubscribers({
    params: { level1ResourceId: level1WRS?.id },
    options: { enabled: !!level1WRS?.id },
  });
  const subscriberOptions = React.useMemo(
    () => toSubscriberOptions(subscribers),
    [subscribers],
  );

  // The scheme's extraction points (offtakes) for the offtake-cell select, fetched
  // once for the whole grid (each preloaded with its subscriber + active meter, plus
  // group / sequence). Each cell filters this shared list to its own row's subscriber
  // and cascades the selected offtake's meter / group / sequence onto the row.
  const { data: extractionPoints } = useAllExtractionPoints({
    params: { level1ResourceId: level1WRS?.id },
    options: { enabled: !!level1WRS?.id, refetchOnWindowFocus: false },
  });

  // Float the rows that still need attention above the clean / already-ledgered ones
  // (see orderTrialRunRows). Drives both the rendered grid and the trial-run download so
  // they match.
  const orderedDisplayRows = React.useMemo(
    () => orderTrialRunRows(displayRows),
    [displayRows],
  );

  // Any field that the trial run validates against the DB belongs in the signature
  // so an edit re-checks the row. group/seq are included (a mismatch is a warning the
  // server resolves); the row note is deliberately excluded — it never affects trial
  // results, so editing it must not trigger a re-run.
  const trialRunSig = rows
    .map(
      r =>
        `${r.id}:${r.rowData.reading}:${r.rowData.readAt}:${r.rowData.serialNo ?? ""}:${r.rowData.extractionPointName}:${r.rowData.accountNumber}:${r.rowData.group ?? ""}:${r.rowData.seq ?? ""}`,
    )
    .join("|");

  React.useEffect(() => {
    if (rows.length === 0) return;
    const handle = setTimeout(() => {
      runTrialRun()
        .catch(() => {})
        .finally(() => setHasRunTrial(true));
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialRunSig]);

  // Build an error toast. AdonisJS 422s arrive as { errors: [{ field, message }] } with no
  // top-level message, so the bare {{error}} interpolation would only show "Request failed
  // with status code 422". When per-field messages exist we list them and drop that noise;
  // any other failure keeps the server/axios message inline as before.
  const apiErrorToast = (messageKey: string, error: any): React.ReactNode => {
    const fieldMessages = getValidationMessages(error);
    return (
      <>
        <p>
          {t(messageKey, {
            error: fieldMessages.length
              ? ""
              : error?.response?.data?.message || error?.message,
          })}
        </p>
        {fieldMessages.length ? (
          <ul className="list-disc pl-4">
            {fieldMessages.map(text => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ) : null}
      </>
    );
  };

  const handleManualIntervention = async (params: {
    calculation: string;
    volumeExtracted: number;
    rowId: string;
  }) => {
    try {
      await applyManualIntervention(params.rowId, {
        calculation: params.calculation,
        volumeExtracted: params.volumeExtracted,
      });
    } catch (error: any) {
      toastError(
        apiErrorToast("bulk_meter_upload.message.row_save_error", error),
      );
    }
  };

  // Commit an inline cell edit. editRow persists the field for a saved draft or keeps
  // it local for a fresh upload; either way the trialRunSig change re-runs the trial.
  const handleInlineCommit = async (
    rowId: string,
    rowData: MeterUploadRowData,
  ) => {
    try {
      await editRow(rowId, rowData);
    } catch (error: any) {
      toastError(
        apiErrorToast("bulk_meter_upload.message.row_save_error", error),
      );
    }
  };

  // Insert a blank editable row inline (the add-row modal is retired). It joins the
  // trial run like any other row — its inline cells are immediately editable, and the
  // debounced re-check picks it up once its required fields are filled in.
  const handleAddRow = async () => {
    try {
      await addRow(buildNewRow());
    } catch (error: any) {
      toastError(
        apiErrorToast("bulk_meter_upload.message.row_save_error", error),
      );
    }
  };

  const persistEvidence = async (meterUploadIdToAttach: string) => {
    if (!files?.length) return;
    await uploadEvidences({
      description: t("bulk_meter_upload.upload_description", {
        level1WRSName: level1WRS?.name,
      }),
      references: [
        {
          referenceId: meterUploadIdToAttach,
          referenceTable: DBTables.MeterUploads,
        },
        {
          referenceId: level1WRS?.id,
          referenceTable: DBTables.WRSHierarchies,
        },
      ],
    });
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      const id = await saveDraft();
      if (id && !meterUploadId) {
        await persistEvidence(id);
      }
      toastSuccess(t("bulk_meter_upload.message.draft_saved"));
    } catch (error: any) {
      toastError(
        apiErrorToast("bulk_meter_upload.message.draft_save_error", error),
      );
    }
    setSubmitting(false);
  };

  const handleDeleteRow = async () => {
    if (!deletingRowId) return;
    setIsDeletingRow(true);
    try {
      await deleteRow(deletingRowId);
      setDeletingRowId(null);
    } catch (error: any) {
      toastError(
        apiErrorToast("bulk_meter_upload.message.row_save_error", error),
      );
    }
    setIsDeletingRow(false);
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const id = meterUploadId ?? (await saveDraft());
      if (id && !meterUploadId) {
        await persistEvidence(id);
      }
      await ledger(id);

      // FR-1.1 + FR-1 invariant: flush optional NoteStep draft AFTER the entity commits.
      // Independent of the (now detached) row finalize; failure only warns.
      if (id) {
        const noteResult = await flushNoteStepDraft(
          draftNote,
          DBTables.MeterUploads,
          id,
        );
        if (noteResult.kind === "failed") {
          toastWarning(
            t("notes.step.flush_failed", {
              artifact: t("bulk_meter_upload.title.upload"),
            }),
          );
        }
      }

      // One-shot ledger: the request is fired and detached, so there's nothing to wait on
      // here. Surface the "started" acknowledgment modal; closing it redirects back to the
      // scheme's bulk-upload list (tab #5) via handleCancel, and the detached finalize
      // notifies the operator when it completes. (A thrown ledger() never reaches here,
      // leaving the operator on the grid to retry.)
      setLedgerStarted(true);
    } catch (error: any) {
      toastError(
        apiErrorToast("bulk_meter_upload.message.ledger_error", error),
      );
    }
    setShowConfirmModal(false);
    setSubmitting(false);
  };

  const tableFields = [
    ...(IS_SEQWATER
      ? [
          { title: t("bulk_meter_upload.table.group"), name: "group" },
          { title: t("bulk_meter_upload.table.seq"), name: "seq" },
        ]
      : []),
    { title: t("bulk_meter_upload.table.subscriber"), name: "accountNumber" },
    {
      title: t("bulk_meter_upload.table.extraction_point_name"),
      name: "extractionPointName",
    },
    {
      title: t("bulk_meter_upload.table.meter_serial_no"),
      name: "serialNo",
    },
    { title: t("bulk_meter_upload.table.meter_read"), name: "reading" },
    { title: t("bulk_meter_upload.table.date"), name: "readAt" },
    {
      title: t("bulk_meter_upload.table.volume_extracted"),
      name: "volumeExtracted",
    },
    { title: t("bulk_meter_upload.table.note"), name: "note" },
    { title: t("bulk_meter_upload.table.status"), name: "tag" },
    { title: "", name: "view" },
  ];

  const errorClassName = "bg-red-50";
  const warningClassName = "bg-yellow-50";

  const tableData = orderedDisplayRows.map((dr: TrialRunDisplayRow) => {
    const errors = dr.errors;
    const volumeExtracted = formatVolume(dr.trial.volumeExtracted ?? 0);
    // Read, date and serial edit inline on non-ledgered rows; ledgered rows are
    // locked read-only. The cell holds its own value and commits on blur. In
    // view mode (?mode=view) every cell is locked regardless of row status.
    const editable = isRowEditable(dr.status) && !readOnly;
    return {
      // Stable row identity for the Table's rowKey: the attention-first sort reorders
      // rows as trial results land, and index-keyed <tr>s would rebind a focused edit
      // cell's local state to whichever row sank into that position (wrong-row commit).
      id: dr.id,
      group: (
        <InlineEditCell
          field="group"
          rowData={dr.rowData}
          editable={editable}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      seq: (
        <InlineEditCell
          field="seq"
          rowData={dr.rowData}
          editable={editable}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      accountNumber: (
        <SubscriberSelectCell
          rowData={dr.rowData}
          options={subscriberOptions}
          editable={editable}
          // The offtake's real owner (server truth) and whether the picked account
          // disagrees with it — drives the inline mismatch hint in place of the old
          // duplicate subscriber-name column.
          ownerName={dr.trial.subscriberName}
          mismatch={errors.accountNumber}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      extractionPointName: (
        <OfftakeSelectCell
          rowData={dr.rowData}
          extractionPoints={extractionPoints}
          editable={editable}
          isSeqwater={IS_SEQWATER}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      serialNo: (
        <InlineEditCell
          field="serialNo"
          rowData={dr.rowData}
          editable={editable}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      reading: (
        <InlineEditCell
          field="reading"
          rowData={dr.rowData}
          editable={editable}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      readAt: (
        <InlineEditCell
          field="readAt"
          rowData={dr.rowData}
          editable={editable}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      volumeExtracted: (
        <div className="flex gap-2 items-center">
          <span>{volumeExtracted}</span>
          {dr.trial.calculation ? (
            <DeclarationTag value={dr.trial.calculation} />
          ) : null}
        </div>
      ),
      note: (
        <RowNoteCell
          rowData={dr.rowData}
          editable={editable}
          declarationId={dr.declarationId}
          onCommit={rowData => handleInlineCommit(dr.id, rowData)}
        />
      ),
      tag: <TrialRunTag status={trialRunDisplayStatus(dr)} />,
      view: (
        <Select
          placeholder={t("common.actions")}
          className="bulk_upload_row_actions"
          classNamePrefix="bulk_upload_row_actions"
          options={[
            { label: t("common.view"), value: "view" },
            ...(dr.status !== MeterUploadRowStatus.Success && !readOnly
              ? [{ label: t("common.delete"), value: "delete" }]
              : []),
          ]}
          onChange={e => {
            if (e?.value === "view") setViewDetailRowId(dr.id);
            else if (e?.value === "delete") setDeletingRowId(dr.id);
          }}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          isSearchable={false}
          value={null}
        />
      ),
      cellClassName: {
        volumeExtracted: errors.requireManualIntervention
          ? errorClassName
          : "bg-gray-50",
        group: errors.group ? warningClassName : "",
        seq: errors.seq ? warningClassName : "",
        accountNumber: errors.accountNumber ? errorClassName : "",
        extractionPointName:
          errors.extractionPointName ||
          errors.extractionPointInactive ||
          errors.extractionPointNotFound
            ? errorClassName
            : "",
        serialNo: errors.serialNo ? errorClassName : "",
        reading:
          errors.duplicate || errors.requireManualIntervention
            ? errorClassName
            : "",
        readAt: errors.duplicate ? errorClassName : "",
      },
    };
  });

  const detailDr = viewDetailRowId
    ? displayRows.find(d => d.id === viewDetailRowId)
    : undefined;
  const detailRow = viewDetailRowId
    ? rows.find(r => r.id === viewDetailRowId)
    : undefined;

  const details = detailDr
    ? {
        upload: detailRow?.rowData ?? detailDr.rowData,
        ledger: {
          ...detailDr.trial,
          // group/seq come from the server trial (the offtake's real values) so the
          // "Ledger details" column shows ledger truth — distinct from the file value
          // it's compared against, which the "File details" column shows.
          group: detailDr.trial.group,
          seq: detailDr.trial.seq,
          accountNumber: detailDr.rowData.accountNumber,
          extractionPointName: detailDr.rowData.extractionPointName,
          serialNo: detailDr.rowData.serialNo,
          error: detailDr.errors,
        },
      }
    : undefined;

  const successCount = rows.filter(
    r => r.status === MeterUploadRowStatus.Success,
  ).length;
  const ledgerableCount = displayRows.filter(
    dr =>
      dr.displayStatus !== TrialRunDisplayStatus.Error &&
      dr.displayStatus !== TrialRunDisplayStatus.Ledgered,
  ).length;
  const errorCount = displayRows.filter(
    dr => dr.displayStatus === TrialRunDisplayStatus.Error,
  ).length;

  return (
    <>
      <div className="flex flex-col grow gap-6 p-6">
        <div className="flex justify-between items-center">
          <Heading light>
            {t("bulk_meter_upload.title.trial_run")} {displayRows.length}{" "}
            {t("common.records")}
            {parentStatus !== MeterUploadStatus.Draft && (
              <span className="ml-3 text-sm">
                <TrialRunTag
                  status={
                    parentStatus === MeterUploadStatus.Completed
                      ? "success"
                      : "warning"
                  }
                />
              </span>
            )}
          </Heading>
          <div
            className={classNames("flex gap-2", { "mr-6": !isExpandInfoPanel })}
          >
            {!readOnly && (
              <button
                className="btn-outline-primary text-sm rounded"
                onClick={handleAddRow}
              >
                {t("bulk_meter_upload.add_row")}
              </button>
            )}
            {displayRows.length > 0 ? (
              <button
                className="btn-primary text-sm rounded"
                onClick={() => {
                  exportData(orderedDisplayRows, t);
                }}
              >
                {t("bulk_meter_upload.download_trial_run")}
              </button>
            ) : null}
          </div>
        </div>

        <Table
          fields={tableFields}
          data={tableData}
          rowKey="id"
          // Spinner only until the first trial run settles. Later re-checks (the
          // debounced re-run after an inline edit) keep the existing grid in place so
          // the spinner never blanks the row the user is editing.
          loading={displayRows.length > 0 && !hasRunTrial}
          stickyHeader
        />
      </div>

      <footer className="flex gap-4 border-t border-gray-300 p-6">
        {!readOnly && (
          <>
            <button
              type="button"
              className="btn-outline-primary"
              onClick={stepHelpers.goToPrevStep}
            >
              {t("common.prev_step")}
            </button>
            <button
              type="button"
              className="btn-outline-primary"
              onClick={handleSaveDraft}
              disabled={submitting || isLedgering || displayRows.length === 0}
            >
              {t("bulk_meter_upload.save_as_draft")}
            </button>
            <button
              className="btn-primary"
              onClick={() => setShowConfirmModal(true)}
              disabled={
                submitting ||
                isLedgering ||
                isTrialRunning ||
                ledgerableCount === 0
              }
            >
              {t("common.ledger")}
              {successCount > 0 ? ` (${successCount}/${rows.length})` : ""}
            </button>
          </>
        )}
        <button className="btn-outline-primary" onClick={handleCancel}>
          {readOnly ? t("common.close") : t("common.cancel")}
        </button>
      </footer>

      <ConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirm}
        isSubmitting={submitting}
      >
        {t("bulk_meter_upload.ledger_confirm_summary", {
          successCount: ledgerableCount,
          errorCount,
        })}
      </ConfirmModal>

      <ConfirmModal
        open={!!deletingRowId}
        onClose={() => setDeletingRowId(null)}
        onConfirm={handleDeleteRow}
        isSubmitting={isDeletingRow}
        confirmText={t("common.delete")}
      >
        {t("bulk_meter_upload.delete_row_confirm")}
      </ConfirmModal>

      {/* Post-ledger acknowledgment: single Close button (no onClose → no X/Escape) so the
          only way out is Close, which redirects to the scheme's bulk-upload list. */}
      <ConfirmModal
        open={ledgerStarted}
        onConfirm={handleCancel}
        confirmText={t("common.close")}
      >
        {t("bulk_meter_upload.message.ledger_started")}
      </ConfirmModal>

      {details && detailDr ? (
        <TrialRunDetailsModal
          onClose={() => setViewDetailRowId(null)}
          onClick={calculation => {
            const volumeExtracted =
              calculation === READING_CALCULATION.CLICK_OVER
                ? detailDr.trial.clickOver -
                  detailDr.trial.lastReading +
                  convertMLToLiter(detailDr.rowData.reading)
                : 0;

            handleManualIntervention({
              calculation,
              volumeExtracted,
              rowId: detailDr.id,
            });
            setViewDetailRowId(null);
          }}
          data={details}
          status={trialRunDisplayStatus(detailDr)}
          readOnly={readOnly}
        />
      ) : null}
    </>
  );
};

const trialRunDisplayStatus = (
  dr: TrialRunDisplayRow,
): "success" | "warning" | "error" | "ledgered" => {
  if (dr.displayStatus === TrialRunDisplayStatus.Ledgered) return "ledgered";
  if (
    dr.displayStatus === TrialRunDisplayStatus.Failed ||
    dr.displayStatus === TrialRunDisplayStatus.Error
  )
    return "error";
  if (dr.displayStatus === TrialRunDisplayStatus.Warning) return "warning";
  return "success";
};

const exportData = (drs: TrialRunDisplayRow[], t: any) => {
  const result = drs.map(dr => {
    const errors = dr.errors;
    return {
      ...(IS_SEQWATER
        ? {
            [t("extraction_point.group")]: dr.rowData.group,
            [t("extraction_point.sequence")]: dr.rowData.seq,
          }
        : {}),
      [t("subscriber.account_number")]: dr.rowData.accountNumber,
      [t("bulk_meter_upload.ledger.subscriber.name")]: dr.trial.subscriberName,
      [t("common.extraction_point")]: dr.rowData.extractionPointName,
      [t("meter.serial_no")]: dr.rowData.serialNo,
      [t("bulk_meter_upload.table.meter_read")]: dr.rowData.reading,
      [t("bulk_meter_upload.table.date")]: formatDate(new Date(dr.rowData.readAt)),
      [t("bulk_meter_upload.ledger.reading")]: convertLiterToML(
        dr.trial.lastReading,
      ),
      [t("bulk_meter_upload.table.volume_extracted")]: formatVolume(
        dr.trial.volumeExtracted,
      ),
      [t("bulk_meter_upload.ledger.date")]: dr.trial.readAt
        ? formatDate(new Date(dr.trial.readAt))
        : "",
      [t("bulk_meter_upload.manual_intervention")]: dr.trial.calculation
        ? t(`declaration.${dr.trial.calculation}`, dr.trial.calculation)
        : "",
      [t("bulk_meter_upload.table.status")]: t(
        `bulk_meter_upload.display_status.${dr.displayStatus}`,
      ),
      [t("common.error")]: getError(errors, t),
    };
  });

  downloadAsCSV({
    data: result,
    fileName: "bulk_meter_upload_trial_run_" + Date.now(),
  });
};

const getError = (errors: TrialRunRowErrors, t: any) => {
  const result: string[] = [];
  if (IS_SEQWATER && errors.group)
    result.push(t("bulk_meter_upload.validation.mismatch.group"));
  if (IS_SEQWATER && errors.seq)
    result.push(t("bulk_meter_upload.validation.mismatch.seq"));
  if (errors.accountNumber)
    result.push(t("bulk_meter_upload.validation.mismatch.subscriber"));
  if (errors.extractionPointName)
    result.push(t("bulk_meter_upload.validation.mismatch.extraction_point"));
  if (errors.extractionPointInactive)
    result.push(t("bulk_meter_upload.validation.extraction_point.inactive"));
  if (errors.extractionPointNotFound)
    result.push(t("bulk_meter_upload.validation.extraction_point.not_found"));
  if (errors.serialNo)
    result.push(t("bulk_meter_upload.validation.mismatch.meter"));
  if (errors.duplicate)
    result.push(t("bulk_meter_upload.validation.duplicate"));
  if (errors.requireManualIntervention)
    result.push(t("bulk_meter_upload.validation.manual_intervention"));
  if (errors.clickOver)
    result.push(t("bulk_meter_upload.validation.click_over"));
  return result.join(", ");
};

export default TrialRun;
